import PropTypes from 'prop-types'
import {ComponentName} from "./consts-types"

export const TypeColors = PropTypes.shape({
  foreground: PropTypes.string,
  background: PropTypes.string
})

export const TypeCTA = PropTypes.shape({
  label: PropTypes.string,
  url: PropTypes.string
})


export const TypeFooter = PropTypes.shape({
  address: PropTypes.string,
  links_after_address: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape({
      title: PropTypes.string.isRequired,
      link: PropTypes.string.isRequired
    })),
    PropTypes.bool
  ]),
  menu: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    link: PropTypes.string,
    featured: PropTypes.bool.isRequired
  })),
  text: PropTypes.string
})


/* DescriptionLists */
export const TypeDescriptionList = PropTypes.shape({
  header: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string)
})


/* ListItems */
export const TypeListItemDataList = PropTypes.arrayOf(PropTypes.string)

export const TypeListItem = PropTypes.shape({
  category: PropTypes.string,
  component_id: PropTypes.string,
  colors: TypeColors,
  cta_primary: TypeCTA,
  cta_secondary: TypeCTA,
  data_list: TypeListItemDataList,
  event_date_utc: PropTypes.string,
  header_bold: PropTypes.string,
  header_light: PropTypes.string,
  header_sub: PropTypes.string,
  hide_publishing_date: PropTypes.bool,
  id: PropTypes.number,
  image_url: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  manchet: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
})

export const TypeListItems = PropTypes.arrayOf(TypeListItem)


export const TypeSortListItem = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired
})

export const TypeTagListItem = PropTypes.shape({
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired
})
export const TypeTagsList = PropTypes.arrayOf(TypeTagListItem)

export const TypeImage = PropTypes.shape({
  caption: PropTypes.string.isRequired,
  image_url: PropTypes.string.isRequired,
  iiif_url: PropTypes.string
})

export const TypeInfoCampaign = TypeListItem

export const TypeReview = PropTypes.shape({
  quote: PropTypes.string,
  source: PropTypes.string,
  stars: PropTypes.string
})

export const TypeInfoList = PropTypes.shape({
  title: PropTypes.string,
  items: TypeListItems
})

export const TypeMenuListItem = PropTypes.shape({
  title: PropTypes.string.isRequired,
  url: PropTypes.string,
  type: PropTypes.string,
  slug: PropTypes.string.isRequired,
  target: PropTypes.string,
  active: PropTypes.bool,
  featured: PropTypes.bool,
  active_child: PropTypes.bool,
  children: PropTypes.array
})

export const TypeMenuList = PropTypes.arrayOf(TypeMenuListItem)


export const TypeBranchMenuItem = PropTypes.shape({
  url: PropTypes.string,
  header_bold: PropTypes.string.isRequired
})


export const TypeComponentContent = PropTypes.arrayOf(PropTypes.shape({
  component_id: PropTypes.string.isRequired
}))


export const TypeOpeningHours = PropTypes.shape({
  message: PropTypes.string,
  link: PropTypes.string
})


export const TypeRoute = PropTypes.shape({
  id: PropTypes.number.isRequired,
  colors: TypeColors,
  type: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  link: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  excerpt: PropTypes.string.isRequired,
  requires_password: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  i18n: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  site_data: PropTypes.shape({
    site_title: PropTypes.string.isRequired,
    is_front_page: PropTypes.bool.isRequired,
  }),
  opening_hours: TypeOpeningHours,
  menu: TypeMenuList.isRequired,
  components: TypeComponentContent.isRequired,
  footer: TypeFooter,
  cookie_box: PropTypes.shape({
    show: PropTypes.bool,
    message: PropTypes.string.isRequired,
    link: PropTypes.string.isRequired,
    link_text: PropTypes.string.isRequired
  })
})


/* Top-level Components */

export const TypeComponentVideo = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.video]).isRequired,
  embed: PropTypes.string,
  url: PropTypes.string.isRequired
})

export const TypeGalleryItem = PropTypes.shape({
  caption: PropTypes.string,
  image_url: PropTypes.string.isRequired
})

export const TypeComponentGallery = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.gallery]).isRequired,
  title: PropTypes.string,
  items: PropTypes.arrayOf(TypeGalleryItem).isRequired
})

export const TypeComponentListFeatured = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.featured_items]).isRequired,
  title: PropTypes.string,
  items: TypeListItems.isRequired,
  num_inview: PropTypes.number
})

export const TypeComponentListFiltered = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.list_items, ComponentName.search_items]).isRequired,
  title: PropTypes.string,
  list_type: PropTypes.string,
  items: TypeListItems.isRequired,
  pre_selected_tag: PropTypes.oneOfType([TypeTagListItem, PropTypes.string]),
  sortings: PropTypes.arrayOf(TypeSortListItem),
  tags: TypeTagsList
})

export const TypeComponentRelated = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.related_content]).isRequired,
  title: PropTypes.string,
  items: TypeListItems.isRequired,
  cta: TypeCTA,
  colors: TypeColors
})

export const TypeComponentDescriptionLists = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.description_list]).isRequired,
  header: PropTypes.string,
  subheader: PropTypes.string,
  items: PropTypes.arrayOf(TypeDescriptionList).isRequired,
})

export const TypeComponentRichText = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.rich_text]).isRequired,
  header: PropTypes.string,
  subheader: PropTypes.string,
  content: PropTypes.string.isRequired,
  show_border: PropTypes.bool
})

export const TypeComponentAudio = PropTypes.shape({
  data: PropTypes.shape({
    url: PropTypes.string.isRequired,
    title: PropTypes.string
  }),
  colors: TypeColors
})

export const TypeComponentAudioPlayer = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.audioplayer]).isRequired,
  url: PropTypes.string.isRequired,
  image_url: PropTypes.string,
  title: PropTypes.string
})

export const TypeComponentSoundCloudPlayer = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.audio]).isRequired,
  header: PropTypes.string,
  subheader: PropTypes.string,
  data: PropTypes.shape({
    url: PropTypes.string.isRequired,
    title: PropTypes.string
  })
})

export const TypeComponentBranchMenu = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.subpages]).isRequired,
  items: PropTypes.arrayOf(TypeBranchMenuItem).isRequired
})

export const TypeComponentCampaign = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.campaign]).isRequired,
  display: PropTypes.oneOf(['left', 'right', 'fullscreen', 'fullscreen_frame']).isRequired,
  category: PropTypes.string,
  image_url: PropTypes.string,
  header_bold: PropTypes.string,
  header_light: PropTypes.string,
  header_sub: PropTypes.string,
  manchet: PropTypes.string,
  data_list: TypeListItemDataList,
  data: PropTypes.number,
  cta_primary: TypeCTA,
  cta_secondary: TypeCTA,
  colors: TypeColors
})

export const TypeComponentHero = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.frontpage_hero, ComponentName.article_hero, ComponentName.section_hero]).isRequired,
  image_url: PropTypes.string,
  category: PropTypes.string,
  header_bold: PropTypes.string,
  header_light: PropTypes.string,
  header_sub: PropTypes.string,
  manchet: PropTypes.string,
  data_list: TypeListItemDataList,
  cta_primary: TypeCTA,
  cta_secondary: TypeCTA,
  colors: TypeColors,
  reviews: TypeReview,
  date: PropTypes.string,
  hide_publishing_date: PropTypes.bool
})

export const TypeGuideMenuItem = PropTypes.shape({
  guide_menu_link: PropTypes.shape({
    title: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
    target: PropTypes.string
  }).isRequired,
  guide_menu_icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired
})

export const TypeComponentGuideMenu = PropTypes.shape({
  component_id: PropTypes.oneOf([ComponentName.guide_menu]).isRequired,
  items: PropTypes.arrayOf(TypeGuideMenuItem).isRequired
})




// WEBPACK FOOTER //
// ./src/constants/consts-prop-types.js